import React, { FC, memo, ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useFormContext } from '../../../../Vendor/react-hook-form/dist/index.ie11'
import { OutlinedTextInput } from '../Inputs/OutlinedTextInput'
import IconButton, { IconButtonType } from '../../../../React/Common/Components/IconButton'
import { IconName } from '../../../../NonReact/constants/React/Icon/Constants'
import { EMPTY_STRING } from '../../../../NonReact/constants/Common'
import { Rules } from '../../../../React/Common/Types/Types'
import { ValidationErrors } from '../../../../React/Common/Components/ValidationErrors/ValidationErrors'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { ErrorLanguage } from '../../../../../i18n'

type Props = {
    name: string
    language: CLanguageHandler
    rules?: Rules
    unique?: boolean
    viewOnly?: boolean
}

type Item = { value: string }

type ArrayItemProps = {
    name: string
    index: number
    value: string
    rules?: Rules
    onChange: (idx: number, val: string) => void
    onRemove: (idx: number) => void
    onTrigger: () => void
    viewOnly?: boolean
}

const ArrayItem: FC<ArrayItemProps> = memo(
    ({ name, index, value, rules, onChange, onRemove, onTrigger, viewOnly }: ArrayItemProps): ReactElement => (
        <div className='aif-item'>
            <ValidationErrors name={`${name}[${index}].value`}>
                <OutlinedTextInput
                    disabled={viewOnly}
                    inputName={`${name}[${index}].value`}
                    value={value}
                    rules={rules}
                    adornmentEnd={
                        !viewOnly ? (
                            <IconButton
                                iconName={IconName.X}
                                type={IconButtonType.BUTTON}
                                onClick={(): void => {
                                    onRemove(index)
                                    onTrigger()
                                }}
                            />
                        ) : (
                            <></>
                        )
                    }
                    onChange={(newValue: string): void => {
                        onChange(index, newValue)
                        onTrigger()
                    }}
                />
            </ValidationErrors>
        </div>
    )
)

export const ArrayInputField: FC<Props> = ({
    name,
    rules,
    unique = false,
    language,
    viewOnly
}: Props): ReactElement => {
    const { getValues, setValue, trigger } = useFormContext()
    const initial: Item[] = getValues(name) || []
    const [items, setItems] = useState<Item[]>(initial)

    const triggerAll = useCallback((): void => {
        items.forEach((_: Item, i: number): void => {
            trigger(`${name}[${i}].value`)
            return undefined
        })
    }, [JSON.stringify(items), name])

    const validateUnique = useCallback(
        (val: string): Definable<string | boolean> => {
            if (!unique) return true
            const count: number = items.filter((i: Item): boolean => i.value === val).length
            return count > 1 ? language.get(ErrorLanguage.Validations_MustBeUnique) : true
        },
        [JSON.stringify(items), unique, language]
    )

    const mergedRules = useMemo<Definable<Rules>>((): Rules => {
        if (!rules && !unique) return undefined
        return {
            ...rules,
            validate: {
                ...(rules?.validate ?? {}),
                unique: validateUnique
            }
        }
    }, [rules, unique, validateUnique])

    useEffect((): void => {
        setValue(name, items, { shouldValidate: true, shouldDirty: true })
    }, [JSON.stringify(items), name])

    const handleAdd = useCallback((): void => {
        setItems((prev: Item[]): Item[] => [...prev, { value: EMPTY_STRING }])
        setTimeout(triggerAll)
    }, [triggerAll])

    const handleRemove = useCallback((idx: number): void => {
        setItems((prev: Item[]): Item[] => prev.filter((_: Item, i: number): boolean => i !== idx))
    }, [])

    const handleChange = useCallback((idx: number, val: string): void => {
        setItems((prev: Item[]) => prev.map((item: Item, i: number): Item => (i === idx ? { value: val } : item)))
    }, [])

    return (
        <div className='array-input-field'>
            {items.map(
                (item: Item, index: number): ReactElement => (
                    <ArrayItem
                        key={index}
                        name={name}
                        index={index}
                        value={item.value}
                        rules={mergedRules}
                        onChange={handleChange}
                        onRemove={handleRemove}
                        onTrigger={triggerAll}
                        viewOnly={viewOnly}
                    />
                )
            )}
            {viewOnly ? <></> : <IconButton type={IconButtonType.BUTTON} iconName={IconName.ADD} onClick={handleAdd} />}
        </div>
    )
}
